import { ChangeEvent, FC, useEffect } from 'react';
import { useEffectOnce } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import isEmail from 'validator/lib/isEmail';
import { ModalComponentBaseProps, useToast } from '@pcmp/react-providers';
import { Sheets, ModalHeader, ModalBody } from '@ingka/modal';
import TextArea from '@ingka/text-area';
import Button from '@ingka/button';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';

import {
  useGetV1UsersMeQuery,
  usePostV1ContactMutation,
} from '@/redux/apis/frontend-api';
import { useFormField } from '@/lib/hooks/form';
import { useTrackContent, useTrackInteraction } from '@/lib/hooks/tracking';
import { useModalGoToProfile } from '@/lib/hooks/modal';

type ContactSiderProps = ModalComponentBaseProps;

const useContactTracking = () => {
  const [trackContent] = useTrackContent();

  useEffectOnce(() => {
    trackContent({ content: 'contact', type: 'sider' });
  });
};

const ContactSection = () => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const mutation = usePostV1ContactMutation();
  const { data: user } = useGetV1UsersMeQuery();
  const [createContact] = mutation;
  const [trackInteraction] = useTrackInteraction();
  const email = useFormField('', (v) => isEmail(v));
  const content = useFormField('', (v) => v.length > 0);
  const submitIsEnabled = email.isValid && content.isValid;

  const fieldHelpers = {
    email: {
      id: 'helper-email',
      msg: t('page.contact.inputs.email.subtitle'),
      error: t('page.contact.inputs.email.error'),
    },
    message: {
      id: 'helper-message',
      msg: t('page.contact.inputs.message.subtitle'),
    },
  };

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    email.setValue(value);
  };

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    content.setValue(value);
  };
  const handleSendButton = async () => {
    // Do not await this mutation, tracking runs on background
    trackInteraction({
      content: 'contact',
      type: 'click',
      action: 'submit',
    });

    const response = await createContact({
      contactMessage: {
        email: email.value,
        message: content.value,
      },
    });
    const isError = response && 'error' in response;
    const status = isError ? 'error' : 'success';
    const message = t(`page.contact.createFeedbackError.${status}`);
    // @ts-ignore
    pushToast({ message });

    // Reset form
    if (!isError) {
      content.setValue('');
    }
  };

  // Update email from user's profile if input is empty
  useEffect(() => {
    if (!email.value && user?.username) {
      email.setValue(user.username);
    }
  }, [user]);

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold  leading-loose">
          {t('page.contact.title')}
        </h1>
        <p className="mt-[10px] text-base font-normal leading-relaxed">
          {t('page.contact.description')}
        </p>
      </div>

      <div className="w-full mt-[10px]">
        <FormField
          characterLimit={0}
          shouldValidate={email.isDirty}
          fieldHelper={fieldHelpers.email}
          valid={email.isValid}
          validation={{
            id: 'email-id',
            msg: fieldHelpers.email.error,
            type: 'error',
          }}
        >
          <InputField
            iconPosition="leading"
            id="email-id"
            label={t('page.contact.inputs.email.title')}
            type="text"
            req={true}
            data-testid="email-input"
            value={email.value}
            onChange={(event: any) => handleEmailChange(event)}
          />
        </FormField>

        <FormField
          characterLimit={0}
          fieldHelper={fieldHelpers.message}
          shouldValidate={false}
        >
          <TextArea
            className="mt-[10px] "
            id="message"
            label={t('page.contact.inputs.message.title')}
            data-testid="message-input"
            value={content.value}
            onChange={(event: any) => handleMessageChange(event)}
          />
        </FormField>

        <Button
          className="w-full h-14 mt-[0px] text-center text-sm  font-bold leading-tight"
          href=""
          iconPosition="leading"
          text={t('page.contact.send')}
          type="emphasised"
          data-testid="send-feedback-button"
          onClick={handleSendButton}
          disabled={!submitIsEnabled}
        />
      </div>
    </>
  );
};

export const ContactSider: FC<ContactSiderProps> = ({
  handleDismiss,
  ...rest
}) => {
  const { t } = useTranslation();
  const goBack = useModalGoToProfile();

  useContactTracking();

  return (
    <Sheets
      {...rest}
      alignment="right"
      aria-label={t('page.contact.shortTitle')}
      footer={null}
      header={
        <ModalHeader ariaCloseTxt={t('label.close')} backBtnClick={goBack} />
      }
      size="small"
    >
      <ModalBody data-testid="contactSider">
        <ContactSection />
      </ModalBody>
    </Sheets>
  );
};
